import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarDays, Plus, MapPin, Trash2 } from "lucide-react";

export default function Meetings() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", location: "", agenda: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/meetings");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/meetings", form);
      toast.success("Meeting scheduled");
      setForm({ title: "", date: "", location: "", agenda: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not schedule");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this meeting?")) return;
    await api.delete(`/meetings/${id}`);
    load();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <CalendarDays className="w-4 h-4" /> Governance
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Meetings</h1>
        </div>
        {isBoard && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11" data-testid="new-meeting-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Schedule meeting
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Schedule a meeting</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="meeting-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11" data-testid="meeting-title-input" placeholder="Monthly board meeting" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" data-testid="meeting-date-input" />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 h-11" data-testid="meeting-location-input" placeholder="Community room" />
                  </div>
                </div>
                <div>
                  <Label>Agenda</Label>
                  <Textarea value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} className="mt-1.5 min-h-[100px]" data-testid="meeting-agenda-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-full" data-testid="meeting-submit-btn">
                    {saving ? "Scheduling..." : "Schedule meeting"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid gap-3">
        {items.length === 0 && (
          <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="meetings-empty">
            No meetings scheduled.
          </div>
        )}
        {items.map((m) => {
          const past = m.date < today;
          return (
            <div key={m.id} className={`hoa-card p-6 ${past ? "opacity-70" : ""}`} data-testid={`meeting-${m.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex gap-4 items-start">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex flex-col items-center justify-center text-primary flex-shrink-0">
                    <div className="text-xs uppercase font-medium">{new Date(m.date).toLocaleString("en-US", { month: "short" })}</div>
                    <div className="text-2xl font-semibold font-mono leading-none">{new Date(m.date).getDate()}</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{m.title}</h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                      <MapPin className="w-3.5 h-3.5" /> {m.location}
                    </div>
                    {m.agenda && <p className="text-sm text-foreground/70 mt-2 whitespace-pre-line leading-relaxed">{m.agenda}</p>}
                  </div>
                </div>
                {isBoard && (
                  <button onClick={() => del(m.id)} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-meeting-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
